import classNames from 'classnames';
import _ from 'lodash';
import Quill from 'quill/core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useThrottledCallback } from 'use-debounce';
import { AnimatePresence, motion } from 'framer-motion';
import { autoUpdate, size, useFloating } from '@floating-ui/react-dom';
import { useQuill } from '../editor/QuillContext';
import {
  getScrollContainer,
  useListenToPostScroll,
} from './listenToPostScroll';
import styles from './NewPostStickyControl.module.scss';
import { usePostLayoutSize } from './PostLayoutSizeContext';
import TableOfContents from './actionButtons/TableOfContents';
import ConnectedTableOfContents from './actionButtons/ConnectedTableOfContents';
import { usePostTOC } from './PostTOCContext';
import useEventListener from '../utils/useEventListener';
import IconButton from '../components/IconButton';
import tocIcon from '../icons/tableOfContentsNew.svg';
import PopOverContainer from '../components/deprecated/PopOverContainer';
import { PostLayoutActions, PostLayoutTOC } from './PostLayoutContainer';
import Button from '../components/Button';
import Tooltip from '../components/Tooltip';

const ACTIONS_TOP_OFFSET = parseInt(styles.postActionsTopOffset, 10);
const SPRING_TRANSITION = {
  type: 'spring',
  stiffness: 200,
  mass: 1,
  damping: 15,
};

const tallerThanEditor = (quill: Quill, offset: number) => {
  let lastVisibleElementChild = quill.root.lastElementChild;
  while (lastVisibleElementChild instanceof HTMLElement) {
    if (lastVisibleElementChild.offsetParent) {
      return (
        offset >
        lastVisibleElementChild.offsetTop + lastVisibleElementChild.offsetHeight
      );
    }

    lastVisibleElementChild = lastVisibleElementChild.previousElementSibling;
  }

  return false;
};

const useElementHeightObserver = (
  element: HTMLElement | null | undefined,
  setHeight: (height: number) => void,
) => {
  useEffect(() => {
    setHeight(element?.offsetHeight ?? 0);
    if (!element) return undefined;

    const resizeObserver = new ResizeObserver((entries) => {
      const { height } = entries[0]?.contentRect || {};
      if (typeof height === 'number') {
        setHeight(height);
      }
    });
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [element, setHeight]);
};

type WiderLineRange = {
  top: number;
  bottom: number;
  left: number;
};

const useWiderLineRanges = (containerRef: React.RefObject<HTMLElement>) => {
  const VERTICAL_MARGIN = 8;
  const { quill } = useQuill();
  const [ranges, setRanges] = useState<WiderLineRange[]>([]);
  const { callback: throttledUpdateRange, flush } = useThrottledCallback(
    () => {
      const newRanges = quill
        ? Array.from(quill.container.querySelectorAll('.span-full')).flatMap(
            (node) => {
              if (!(node instanceof HTMLElement) || !node.offsetParent) {
                return [];
              }
              const rect = node.getBoundingClientRect();
              const top = node.offsetTop;
              return [
                {
                  left: rect.left,
                  top: top - VERTICAL_MARGIN,
                  bottom: top + node.offsetHeight + VERTICAL_MARGIN,
                },
              ];
            },
          )
        : [];

      if (!_.isEqual(ranges, newRanges)) {
        setRanges(newRanges);
      }
    },
    200,
    { leading: false },
  );

  const forceUpdateRange = useCallback(() => {
    throttledUpdateRange();
    flush();
  }, [throttledUpdateRange, flush]);

  // Images maybe resized after loaded.
  useElementHeightObserver(quill?.root, forceUpdateRange);

  // When the container resizes, the wider lines might not be in the way
  // of the sticky control anymore, therefore we need to recalculate
  useEffect(() => {
    if (!containerRef.current) return undefined;
    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0]?.contentRect || {};
      if (width) {
        forceUpdateRange();
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [containerRef, forceUpdateRange]);

  useEffect(() => {
    if (!quill) return undefined;
    quill.on(Quill.events.TEXT_CHANGE, throttledUpdateRange);
    return () => {
      quill.off(Quill.events.TEXT_CHANGE, throttledUpdateRange);
    };
  }, [quill, throttledUpdateRange]);

  return ranges;
};

interface TocButtonProps {
  onClick: () => void;
  ariaLabel: string;
  hasHeaders: boolean;
}

const TocButton = React.forwardRef<HTMLButtonElement, TocButtonProps>(
  ({ onClick, ariaLabel, hasHeaders }, ref) => {
    const button = (
      <IconButton
        ref={ref}
        onClick={onClick}
        aria-label={ariaLabel}
        className={styles.tocButton}
      >
        {tocIcon}
      </IconButton>
    );

    if (hasHeaders) {
      return <>{button}</>;
    }

    return (
      <Tooltip
        trigger="hover"
        popOverPortal={document.body}
        tooltip="Add headings to your post to automatically add a table of contents"
        placement="right"
      >
        {button}
      </Tooltip>
    );
  },
);

interface StyleModifier {
  static?: boolean;
  top?: number;
  transition?: boolean;
  // CSS transition doesn't support transit from sticky position to static position.
  // In order to achieve this, we need to update the position to the target one and
  // keep the top offset visually the same. And then change the top offset to the
  // target one.
  nextStyles?: Omit<StyleModifier, 'nextStyles'>;
}

interface NewPostStickyControlProps {
  postId: string;
  containerRef: React.RefObject<HTMLElement>;
  postLayoutBannerRef: React.RefObject<HTMLDivElement>;
}

const NewPostStickyControl = ({
  postId,
  containerRef,
  postLayoutBannerRef: postBannerContainerRef,
}: NewPostStickyControlProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { quill } = useQuill();
  const [tocElement, setTOCElement] = useState<HTMLElement | null>(null);
  const [actionsElement, setActionsElement] = useState<HTMLElement | null>(
    null,
  );
  const [tocHeight, setTOCHeight] = useState(0);
  const [actionsHeight, setActionsHeight] = useState(0);
  const [styleModifier, setStyleModifier] = useState<StyleModifier>({});

  const postLayoutSize = usePostLayoutSize();
  const isSticky = !['narrow', 'small'].includes(postLayoutSize);
  const widerLineRanges = useWiderLineRanges(containerRef);

  useElementHeightObserver(tocElement, setTOCHeight);
  useElementHeightObserver(actionsElement, setActionsHeight);

  const middleware = useMemo(() => [size({ padding: 12 })], []);
  const { refs, floatingStyles, update } = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware,
  });

  const { callback: throttledUpdateStaticTop, flush } = useThrottledCallback(
    () => {
      if (styleModifier.nextStyles) return;
      const height = Math.max(actionsHeight, tocHeight);

      if (!quill || !ref.current || height === 0) {
        setStyleModifier({});
        return;
      }

      const bannerTop =
        postBannerContainerRef.current?.getBoundingClientRect().top;
      const quillTop = quill.root.getBoundingClientRect().top;

      const editorTopRelativeToScrollContainer =
        (bannerTop ?? quillTop) -
        (getScrollContainer()?.getBoundingClientRect().top ?? 0);

      const stickyTopRelativeToEditor = Math.max(
        parseInt(
          getComputedStyle(ref.current).getPropertyValue(
            '--post-sticky-control-top',
          ),
          10,
        ) - editorTopRelativeToScrollContainer,
        0,
      );

      const editorTopOffset = bannerTop ? quillTop - bannerTop : 0;
      const ranges = widerLineRanges.map(({ top, bottom, ...rest }) => ({
        top: top + editorTopOffset,
        bottom: bottom + editorTopOffset,
        ...rest,
      }));
      const referenceElement = tocElement ?? actionsElement;
      const rightBoundary =
        referenceElement?.getBoundingClientRect().right ?? 0;

      if (
        ranges.every(
          (range) =>
            range.left > rightBoundary ||
            stickyTopRelativeToEditor - ACTIONS_TOP_OFFSET >= range.bottom ||
            stickyTopRelativeToEditor + height + ACTIONS_TOP_OFFSET <=
              range.top,
        ) &&
        // Sticky elements don't occupy space
        !tallerThanEditor(quill, stickyTopRelativeToEditor + height)
      ) {
        if (styleModifier.top) {
          setStyleModifier({
            top: styleModifier.top - stickyTopRelativeToEditor,
            nextStyles: { transition: true },
          });
        }
        return;
      }

      const rangesAfter = ranges.filter(
        ({ bottom }) =>
          bottom + ACTIONS_TOP_OFFSET >= stickyTopRelativeToEditor,
      );

      _.forEach(rangesAfter, ({ bottom }, index) => {
        const top = bottom + ACTIONS_TOP_OFFSET;
        if (
          rangesAfter
            .slice(index + 1)
            .every(
              (nextRange) => top + height + ACTIONS_TOP_OFFSET <= nextRange.top,
            )
        ) {
          const newStyle = {
            static: true,
            top: bottom,
            transition: true,
          } as const;
          if (!styleModifier.static) {
            setStyleModifier({
              static: true,
              top: stickyTopRelativeToEditor,
              nextStyles: newStyle,
            });
          } else if (styleModifier.top !== newStyle.top) {
            setStyleModifier(newStyle);
          }
          return false;
        }

        return true;
      });
    },
    100,
    { leading: false },
  );

  useListenToPostScroll(throttledUpdateStaticTop);

  useEffect(() => {
    flush();
    throttledUpdateStaticTop();
  }, [
    flush,
    throttledUpdateStaticTop,
    tocHeight,
    actionsHeight,
    widerLineRanges,
  ]);

  const { nextStyles } = styleModifier;
  useEffect(() => {
    if (nextStyles && ref.current) {
      // Force a repaint to ensure the current styleModifier is applied.
      // This is inspired by https://github.com/reactjs/react-transition-group/blob/740b1dd/src/CSSTransition.js#L194-L199
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      ref.current.scrollTop;
      setStyleModifier(nextStyles);
    }
  }, [nextStyles]);

  const [container, setContainer] = useState<HTMLElement | null>(null);
  const { isTOCOpen, toggleTOC } = usePostTOC();
  useEventListener(container, 'scroll', () => {
    container?.classList.toggle(styles.scrolled, container.scrollTop > 0);
  });

  const [isTOCButtonHovered, setIsTOCButtonHovered] = useState(false);

  return (
    <ConnectedTableOfContents postId={postId}>
      {(tocProps) => {
        const hasHeaders = tocProps.headers.length > 0;
        const isShowingTOC = isSticky && isTOCOpen && hasHeaders;
        const tocButtonAriaLabel = isShowingTOC
          ? 'Hide table of contents'
          : 'Show table of contents';

        return (
          <div
            ref={ref}
            style={
              {
                '--post-sticky-control-position': styleModifier.static
                  ? 'static'
                  : undefined,
                '--post-sticky-control-transition': styleModifier.transition
                  ? 'all'
                  : undefined,
                '--post-sticky-control-offset':
                  typeof styleModifier.top === 'number'
                    ? `${styleModifier.top}px`
                    : undefined,
              } as React.CSSProperties
            }
            className={classNames(styles.container, {
              [styles.isShowingTOC]: isShowingTOC,
            })}
          >
            <PostLayoutTOC className={styles.toc}>
              <AnimatePresence>
                {isShowingTOC && (
                  <motion.nav
                    key="toc"
                    ref={setTOCElement}
                    initial={{ opacity: 0, x: -50, pointerEvents: 'none' }}
                    animate={{
                      opacity: 1,
                      x: 0,
                      transitionEnd: {
                        pointerEvents: 'auto',
                      },
                    }}
                    exit={{ opacity: 0, x: -50, pointerEvents: 'none' }}
                    transition={SPRING_TRANSITION}
                    className={styles.tocContainer}
                  >
                    <h4 className={styles.title}>
                      Table of Contents
                      <Button
                        className={styles.closeAreaButton}
                        aria-label={tocButtonAriaLabel}
                        onClick={toggleTOC}
                      />
                    </h4>
                    <div ref={setContainer} className={styles.content}>
                      <TableOfContents {...tocProps} />
                    </div>
                  </motion.nav>
                )}
              </AnimatePresence>
            </PostLayoutTOC>
            <PostLayoutActions
              className={styles.actionButtons}
              onTransitionEnd={update}
            >
              <div
                ref={setActionsElement}
                onMouseEnter={() => setIsTOCButtonHovered(true)}
                onMouseLeave={() => setIsTOCButtonHovered(false)}
              >
                <TocButton
                  ref={refs.setReference}
                  onClick={() => {
                    if (isSticky && hasHeaders) {
                      toggleTOC();
                    }
                  }}
                  ariaLabel={tocButtonAriaLabel}
                  hasHeaders={hasHeaders}
                />
                <div ref={refs.setFloating} style={floatingStyles}>
                  <AnimatePresence>
                    {hasHeaders && isTOCButtonHovered && !isSticky && (
                      <motion.div
                        className={styles.tocPopOverContainer}
                        initial={{ opacity: 0, y: -50, pointerEvents: 'none' }}
                        animate={{
                          opacity: 1,
                          y: 0,
                          transitionEnd: {
                            pointerEvents: 'auto',
                          },
                        }}
                        exit={{ opacity: 0, y: -50, pointerEvents: 'none' }}
                        transition={SPRING_TRANSITION}
                      >
                        <PopOverContainer width="auto">
                          <div className={styles.tocPopOverContent}>
                            <h4 className={styles.title}>Table of Contents</h4>
                            <TableOfContents {...tocProps} />
                          </div>
                        </PopOverContainer>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              {isShowingTOC && (
                <Button
                  className={styles.closeAreaButton}
                  aria-label={tocButtonAriaLabel}
                  onClick={toggleTOC}
                />
              )}
            </PostLayoutActions>
          </div>
        );
      }}
    </ConnectedTableOfContents>
  );
};

export default NewPostStickyControl;
